import { useEffect, useState, useCallback, useMemo, Fragment } from 'react';
import { apiFetch } from '../fetchInterceptor';
import { useWorkspace } from '../context/WorkspaceContext';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator,
} from './ui/dropdown-menu';
import {
  Shield, Eye, EyeOff, RotateCcw, Search, Sparkles, ChevronDown, BookOpen,
  Save, Trash2, History,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import InfoTooltip from './InfoTooltip';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ROLES = ['owner', 'admin', 'accountant', 'bookkeeper', 'read_only', 'client_portal'];

/**
 * Admin-only panel for customizing per-user tab visibility across the entire
 * app. Two main layers:
 *
 *  1. **Role defaults matrix** — shows which tabs each role can see by
 *     default (out of the box) so admins don't have to memorise it.
 *  2. **Per-user overrides** — for each member, pick a built-in preset
 *     (Bookkeeper, Tax Prep, Front Desk, Advisor) OR flip individual tabs
 *     on/off. Overrides persist in `workspace_members.tab_overrides`.
 *
 * Pulls the canonical tab catalog from `/api/access/tab-catalog` so the UI
 * stays in sync with the server-side `shared/access_catalog.py` source of
 * truth. Adding a new tab in the catalog file makes it appear here
 * automatically.
 */
export default function AccessOverridesPanel() {
  const { currentWorkspace } = useWorkspace();
  const wsId = currentWorkspace?.workspace_id;
  const [members, setMembers] = useState([]);
  const [catalog, setCatalog] = useState({ tabs: [], presets: [] });
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [savingTab, setSavingTab] = useState(null);
  const [applyingPreset, setApplyingPreset] = useState(false);
  const [showDefaultsMatrix, setShowDefaultsMatrix] = useState(false);
  const [auditEvents, setAuditEvents] = useState([]);
  const [showAuditTrail, setShowAuditTrail] = useState(false);
  const [saveAsPresetOpen, setSaveAsPresetOpen] = useState(false);
  const [newPresetLabel, setNewPresetLabel] = useState('');
  const [newPresetDescription, setNewPresetDescription] = useState('');
  const [savingNewPreset, setSavingNewPreset] = useState(false);

  const fetchAll = useCallback(async () => {
    if (!wsId) return;
    setLoading(true);
    try {
      const [overviewRes, catalogRes, auditRes] = await Promise.all([
        apiFetch(`${API_URL}/api/workspaces/${wsId}/access-overview`),
        apiFetch(`${API_URL}/api/access/tab-catalog?workspace_id=${wsId}`),
        apiFetch(`${API_URL}/api/workspaces/${wsId}/access-audit?limit=15`),
      ]);
      if (overviewRes.ok) {
        const data = await overviewRes.json();
        setMembers(data.members || []);
      } else if (overviewRes.status === 403) {
        toast.error('Only workspace owners/admins can view this panel');
      }
      if (catalogRes.ok) {
        const data = await catalogRes.json();
        setCatalog({ tabs: data.tabs || [], presets: data.presets || [] });
      }
      if (auditRes.ok) {
        const data = await auditRes.json();
        setAuditEvents(data.events || []);
      }
    } catch {
      toast.error('Failed to load access controls');
    } finally {
      setLoading(false);
    }
  }, [wsId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const selected = members.find((m) => m.user_id === selectedUserId);
  const overrides = selected?.tab_overrides || {};
  const role = (selected?.role || 'bookkeeper').toLowerCase();

  // Build a section -> tabs map from the canonical catalog
  const sectionedTabs = useMemo(() => {
    const map = new Map();
    for (const t of catalog.tabs) {
      if (!map.has(t.section)) map.set(t.section, []);
      map.get(t.section).push(t);
    }
    return Array.from(map.entries()); // [['AI', [...]], ['Money', [...]], ...]
  }, [catalog.tabs]);

  const defaultAllowed = (tabId) => {
    const tab = catalog.tabs.find((t) => t.id === tabId);
    if (!tab) return true;
    return !!tab.default_by_role?.[role];
  };

  const setOverride = async (tabId, value) => {
    if (!selectedUserId) return;
    setSavingTab(tabId);
    try {
      const r = await apiFetch(
        `${API_URL}/api/workspaces/${wsId}/members/${selectedUserId}/tab-overrides`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ overrides: { [tabId]: value } }),
        },
      );
      if (r.ok) {
        const data = await r.json();
        setMembers((prev) => prev.map((m) =>
          m.user_id === selectedUserId ? { ...m, tab_overrides: data.tab_overrides } : m,
        ));
        toast.success(value === null ? 'Reset to role default' : value ? 'Tab unlocked' : 'Tab hidden');
      } else {
        toast.error('Failed to save override');
      }
    } catch {
      toast.error('Failed to save override');
    } finally {
      setSavingTab(null);
    }
  };

  const applyPreset = async (presetId) => {
    if (!selectedUserId) return;
    setApplyingPreset(true);
    try {
      const r = await apiFetch(
        `${API_URL}/api/workspaces/${wsId}/members/${selectedUserId}/apply-preset`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ preset_id: presetId }),
        },
      );
      if (r.ok) {
        const data = await r.json();
        setMembers((prev) => prev.map((m) =>
          m.user_id === selectedUserId
            ? { ...m, tab_overrides: data.tab_overrides, applied_preset: data.applied_preset }
            : m,
        ));
        const preset = catalog.presets.find((p) => p.id === presetId);
        toast.success(`Applied: ${preset?.label || presetId}`);
      } else {
        toast.error('Failed to apply preset');
      }
    } catch {
      toast.error('Failed to apply preset');
    } finally {
      setApplyingPreset(false);
    }
  };

  /**
   * Save the currently-selected member's tab_overrides as a new workspace
   * preset. The next time the admin onboards a similar role, they pick this
   * preset and apply it in one click — no need to flip 40 tabs.
   */
  const saveAsCustomPreset = async () => {
    if (!selected || !newPresetLabel.trim()) return;
    setSavingNewPreset(true);
    try {
      const show = Object.entries(overrides).filter(([, v]) => v === true).map(([k]) => k);
      const hide = Object.entries(overrides).filter(([, v]) => v === false).map(([k]) => k);
      const r = await apiFetch(
        `${API_URL}/api/workspaces/${wsId}/access-presets`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            label: newPresetLabel.trim(),
            description: newPresetDescription.trim(),
            show,
            hide,
          }),
        },
      );
      if (r.ok) {
        toast.success(`Saved preset: ${newPresetLabel}`);
        setSaveAsPresetOpen(false);
        setNewPresetLabel('');
        setNewPresetDescription('');
        await fetchAll();
      } else {
        toast.error('Failed to save preset');
      }
    } catch {
      toast.error('Failed to save preset');
    } finally {
      setSavingNewPreset(false);
    }
  };

  const deleteCustomPreset = async (presetId) => {
    if (!window.confirm('Delete this preset? It will no longer be available to apply.')) return;
    try {
      const r = await apiFetch(
        `${API_URL}/api/workspaces/${wsId}/access-presets/${presetId}`,
        { method: 'DELETE' },
      );
      if (r.ok) {
        toast.success('Preset deleted');
        await fetchAll();
      } else {
        toast.error('Failed to delete preset');
      }
    } catch {
      toast.error('Failed to delete preset');
    }
  };

  const filteredMembers = search
    ? members.filter((m) =>
        (m.name || m.email || m.user_id || '').toLowerCase().includes(search.toLowerCase()),
      )
    : members;

  if (loading) return <p className="text-sm text-muted-foreground">Loading access controls…</p>;

  return (
    <div className="space-y-4" data-testid="access-overrides-panel">
      {/* ── Role-defaults explainer + matrix toggle ─────────────────────── */}
      <Card className="border-emerald-200/60 bg-emerald-50/30 dark:bg-emerald-950/15">
        <CardContent className="p-4 space-y-2">
          <div className="flex items-start gap-3 flex-wrap">
            <Shield className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-sm">Per-User Access Controls</h3>
              <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                Each role starts with sensible defaults (e.g. <strong>Bookkeepers</strong> can post
                journal entries, <strong>Viewers</strong> can read reports only). Use the controls
                below to override defaults for a specific user — hide individual tabs, grant extra
                access, or apply a one-click preset like <strong>"Tax Preparer"</strong> or
                <strong> "Front Desk"</strong>.
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowDefaultsMatrix((v) => !v)}
              className="gap-1.5"
              data-testid="toggle-role-defaults"
            >
              <BookOpen className="w-3.5 h-3.5" />
              {showDefaultsMatrix ? 'Hide' : 'Show'} role defaults
            </Button>
          </div>
          {showDefaultsMatrix && (
            <div className="mt-3 pt-3 border-t border-emerald-200/60 dark:border-emerald-900/40 overflow-x-auto">
              <table className="w-full text-xs" data-testid="role-defaults-matrix">
                <thead>
                  <tr className="text-left">
                    <th className="pb-1.5 pr-3 font-semibold">Tab</th>
                    {ROLES.map((r) => (
                      <th key={r} className="pb-1.5 px-2 font-semibold text-center capitalize">
                        {r === 'read_only' ? 'Viewer' : r === 'client_portal' ? 'Portal' : r}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sectionedTabs.map(([section, tabs]) => (
                    <Fragment key={section}>
                      <tr>
                        <td colSpan={ROLES.length + 1} className="pt-2 pb-1 text-[10px] uppercase tracking-widest text-emerald-700 dark:text-emerald-400 font-mono">
                          {section}
                        </td>
                      </tr>
                      {tabs.map((tab) => (
                        <tr key={tab.id} className="border-t border-emerald-100/60 dark:border-emerald-900/30">
                          <td className="py-1 pr-3 font-medium">{tab.label}</td>
                          {ROLES.map((r) => {
                            const visible = !!tab.default_by_role?.[r];
                            return (
                              <td key={r} className="py-1 px-2 text-center">
                                {visible ? (
                                  <Eye className="inline w-3.5 h-3.5 text-emerald-600" />
                                ) : (
                                  <EyeOff className="inline w-3.5 h-3.5 text-zinc-400" />
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-4">
        {/* Member list */}
        <Card>
          <CardContent className="p-3 space-y-2">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-sm">Members</h3>
              <InfoTooltip title="Members" testId="info-members" side="bottom">
                <p>Pick a member to customize what they see in the sidebar.</p>
              </InfoTooltip>
            </div>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search…"
                className="pl-8 h-8 text-sm"
                data-testid="member-search"
              />
            </div>
            <div className="space-y-1 max-h-[60vh] overflow-y-auto">
              {filteredMembers.map((m) => {
                const isActive = selectedUserId === m.user_id;
                const customCount = Object.keys(m.tab_overrides || {}).length;
                return (
                  <button
                    key={m.user_id}
                    onClick={() => setSelectedUserId(m.user_id)}
                    className={cn(
                      'w-full text-left px-3 py-2 rounded-md border text-sm transition-colors',
                      isActive
                        ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-950/30'
                        : 'border-transparent hover:bg-muted',
                    )}
                    data-testid={`member-${m.user_id}`}
                  >
                    <div className="flex items-center gap-1.5">
                      <span className="font-medium truncate flex-1">
                        {m.name || m.email || m.user_id}
                      </span>
                      <Badge variant="outline" className="text-[9px] capitalize">
                        {m.role || 'member'}
                      </Badge>
                    </div>
                    {customCount > 0 && (
                      <div className="text-[10px] text-amber-600 mt-0.5">
                        {customCount} custom override{customCount === 1 ? '' : 's'}
                        {m.applied_preset && (
                          <> · preset: <span className="italic">{m.applied_preset}</span></>
                        )}
                      </div>
                    )}
                  </button>
                );
              })}
              {filteredMembers.length === 0 && (
                <p className="text-[11px] text-muted-foreground italic px-1">No members match.</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Tab overrides grid */}
        <Card>
          <CardContent className="p-4 space-y-3">
            {!selected ? (
              <p className="text-sm text-muted-foreground italic">
                Pick a member from the list to customize their tab access.
              </p>
            ) : (
              <>
                <div className="flex items-center gap-2 pb-2 border-b flex-wrap">
                  <h3 className="font-semibold text-base">
                    {selected.name || selected.email || selected.user_id}
                  </h3>
                  <Badge className="text-[10px] capitalize" variant="outline">
                    {selected.role || 'member'}
                  </Badge>
                  <span className="text-xs text-muted-foreground">
                    {Object.keys(overrides).length} custom override{Object.keys(overrides).length === 1 ? '' : 's'}
                  </span>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="default"
                        size="sm"
                        className="ml-auto gap-1.5 bg-violet-600 hover:bg-violet-700"
                        disabled={applyingPreset}
                        data-testid="apply-preset-trigger"
                      >
                        <Sparkles className="w-3.5 h-3.5" />
                        Apply preset
                        <ChevronDown className="w-3.5 h-3.5" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-80">
                      <DropdownMenuLabel>Quick-apply access preset</DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      {catalog.presets.filter((p) => p.builtin !== false).map((p) => (
                        <DropdownMenuItem
                          key={p.id}
                          onClick={() => applyPreset(p.id)}
                          className="flex-col items-start gap-0.5 py-2"
                          data-testid={`preset-${p.id}`}
                        >
                          <span className="font-medium">{p.label}</span>
                          <span className="text-[11px] text-muted-foreground whitespace-normal leading-snug">
                            {p.description}
                          </span>
                        </DropdownMenuItem>
                      ))}
                      {catalog.presets.some((p) => p.builtin === false) && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-emerald-700 dark:text-emerald-400 font-mono">
                            Workspace presets
                          </DropdownMenuLabel>
                          {catalog.presets.filter((p) => p.builtin === false).map((p) => (
                            <DropdownMenuItem
                              key={p.id}
                              onClick={() => applyPreset(p.id)}
                              className="flex items-start justify-between gap-2 py-2"
                              data-testid={`preset-${p.id}`}
                            >
                              <div className="flex-col items-start gap-0.5 flex-1 min-w-0">
                                <div className="font-medium truncate">{p.label}</div>
                                {p.description && (
                                  <div className="text-[11px] text-muted-foreground whitespace-normal leading-snug">
                                    {p.description}
                                  </div>
                                )}
                              </div>
                              <button
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  deleteCustomPreset(p.id);
                                }}
                                className="p-1 rounded hover:bg-red-500/20 text-red-500"
                                aria-label="Delete preset"
                                data-testid={`preset-delete-${p.id}`}
                              >
                                <Trash2 className="w-3 h-3" />
                              </button>
                            </DropdownMenuItem>
                          ))}
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSaveAsPresetOpen(true)}
                    disabled={Object.keys(overrides).length === 0}
                    className="gap-1.5"
                    title={Object.keys(overrides).length === 0 ? 'Set at least one override first' : 'Save this user\'s overrides as a reusable preset'}
                    data-testid="save-as-preset-btn"
                  >
                    <Save className="w-3.5 h-3.5" />
                    Save as preset
                  </Button>
                </div>

                {selected.applied_preset && (
                  <div className="text-xs px-3 py-1.5 rounded-md bg-violet-50 dark:bg-violet-950/30 border border-violet-200 dark:border-violet-900/40 text-violet-800 dark:text-violet-200">
                    <Sparkles className="inline w-3 h-3 mr-1 -mt-0.5" />
                    Preset applied: <strong>{selected.applied_preset}</strong>. Tweak any tab
                    individually below — overrides are saved per-tab.
                  </div>
                )}

                <div className="text-xs text-muted-foreground">
                  Each tab shows the default for the user's role, plus any explicit override.
                  Use <strong>Show</strong> / <strong>Hide</strong> to override, or
                  <strong> Reset</strong> to fall back to the role default.
                </div>

                <div className="space-y-3">
                  {sectionedTabs.map(([section, tabs]) => (
                    <div key={section} className="space-y-1.5">
                      <h4 className="text-[10px] uppercase tracking-widest text-emerald-700 dark:text-emerald-400 font-mono">
                        {section}
                      </h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {tabs.map((t) => {
                          const value = overrides[t.id]; // true | false | undefined
                          const dflt = defaultAllowed(t.id);
                          const effective = value === undefined ? dflt : value;
                          return (
                            <div
                              key={t.id}
                              className={cn(
                                'flex items-center gap-2 px-3 py-2 rounded-md border',
                                value === false
                                  ? 'bg-red-50/40 border-red-200 dark:bg-red-950/20 dark:border-red-900/40'
                                  : value === true
                                  ? 'bg-emerald-50/40 border-emerald-200 dark:bg-emerald-950/20 dark:border-emerald-900/40'
                                  : 'border-border',
                              )}
                              data-testid={`tab-row-${t.id}`}
                            >
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium truncate" title={t.description}>
                                  {t.label}
                                </div>
                                <div className="text-[10px] text-muted-foreground">
                                  {value === undefined && <span>Default: {dflt ? 'visible' : 'hidden'}</span>}
                                  {value === true && <span className="text-emerald-600 font-medium">Override: visible</span>}
                                  {value === false && <span className="text-red-600 font-medium">Override: hidden</span>}
                                  {' · effective: '}
                                  <strong>{effective ? 'visible' : 'hidden'}</strong>
                                </div>
                              </div>
                              <div className="flex gap-1">
                                <Button
                                  size="icon"
                                  variant={value === true ? 'default' : 'outline'}
                                  className={cn('h-7 w-7', value === true && 'bg-emerald-600 hover:bg-emerald-700 text-white')}
                                  onClick={() => setOverride(t.id, true)}
                                  disabled={savingTab === t.id}
                                  title="Show this tab"
                                  data-testid={`tab-show-${t.id}`}
                                >
                                  <Eye className="w-3.5 h-3.5" />
                                </Button>
                                <Button
                                  size="icon"
                                  variant={value === false ? 'default' : 'outline'}
                                  className={cn('h-7 w-7', value === false && 'bg-red-600 hover:bg-red-700 text-white')}
                                  onClick={() => setOverride(t.id, false)}
                                  disabled={savingTab === t.id}
                                  title="Hide this tab"
                                  data-testid={`tab-hide-${t.id}`}
                                >
                                  <EyeOff className="w-3.5 h-3.5" />
                                </Button>
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="h-7 w-7"
                                  onClick={() => setOverride(t.id, null)}
                                  disabled={savingTab === t.id || value === undefined}
                                  title="Reset to role default"
                                  data-testid={`tab-reset-${t.id}`}
                                >
                                  <RotateCcw className="w-3.5 h-3.5" />
                                </Button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* ── Audit trail ───────────────────────────────────────────────── */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-2">
              <History className="w-4 h-4 text-emerald-600" />
              <h3 className="font-semibold text-sm">Recent access changes</h3>
              <Badge variant="outline" className="text-[10px]">
                {auditEvents.length}
              </Badge>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowAuditTrail((v) => !v)}
              data-testid="toggle-audit-trail"
            >
              {showAuditTrail ? 'Hide' : 'Show'} history
              <ChevronDown
                className={cn(
                  'w-3.5 h-3.5 ml-1 transition-transform',
                  showAuditTrail && 'rotate-180',
                )}
              />
            </Button>
          </div>
          {showAuditTrail && (
            <div className="mt-3 space-y-1.5 max-h-72 overflow-y-auto" data-testid="audit-trail-list">
              {auditEvents.length === 0 ? (
                <p className="text-xs text-muted-foreground italic">
                  No access changes recorded yet. As you flip tabs and apply presets, this log
                  will track who changed what and when.
                </p>
              ) : (
                auditEvents.map((e) => (
                  <div
                    key={e.event_id}
                    className="text-xs px-3 py-2 rounded-md border border-border bg-muted/30"
                    data-testid={`audit-event-${e.event_id}`}
                  >
                    <div className="flex items-baseline justify-between gap-2 flex-wrap">
                      <div className="font-medium">
                        <span className="text-emerald-700 dark:text-emerald-400">{e.actor_name}</span>
                        {' '}
                        {e.action === 'preset_applied' ? 'applied preset' : 'updated overrides'}
                        {' for '}
                        <span className="text-violet-700 dark:text-violet-400">{e.target_name}</span>
                      </div>
                      <span className="text-[10px] text-muted-foreground font-mono">
                        {new Date(e.created_at).toLocaleString()}
                      </span>
                    </div>
                    {e.action === 'preset_applied' && (
                      <div className="text-[11px] text-muted-foreground mt-0.5">
                        Preset: <strong>{e.details?.preset_label || e.details?.preset_id}</strong>
                        {' '}({e.details?.override_count || 0} override{e.details?.override_count === 1 ? '' : 's'})
                      </div>
                    )}
                    {e.action === 'tab_overrides_changed' && e.details?.changes && (
                      <div className="text-[11px] text-muted-foreground mt-0.5 space-y-0.5">
                        {e.details.changes.slice(0, 4).map((c, i) => (
                          <div key={i}>
                            {c.action === 'show' && <>Granted access to <strong>{c.tab_id}</strong></>}
                            {c.action === 'hide' && <>Hid <strong>{c.tab_id}</strong></>}
                            {c.action === 'reset' && <>Reset <strong>{c.tab_id}</strong> to role default</>}
                          </div>
                        ))}
                        {e.details.changes.length > 4 && (
                          <div className="text-muted-foreground italic">
                            + {e.details.changes.length - 4} more change{e.details.changes.length - 4 === 1 ? '' : 's'}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Save-as-preset dialog ─────────────────────────────────────── */}
      <Dialog open={saveAsPresetOpen} onOpenChange={setSaveAsPresetOpen}>
        <DialogContent data-testid="save-preset-dialog">
          <DialogHeader>
            <DialogTitle>Save as workspace preset</DialogTitle>
            <DialogDescription>
              Save the current member's tab overrides as a reusable preset. The next time you
              onboard a similar role, you'll be able to apply this in one click instead of
              flipping each tab manually.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1">
              <label className="text-xs font-medium">Preset name</label>
              <Input
                value={newPresetLabel}
                onChange={(e) => setNewPresetLabel(e.target.value)}
                placeholder="e.g. Junior Bookkeeper"
                data-testid="new-preset-label"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium">Description (optional)</label>
              <Input
                value={newPresetDescription}
                onChange={(e) => setNewPresetDescription(e.target.value)}
                placeholder="What this role does"
                data-testid="new-preset-description"
              />
            </div>
            <div className="text-[11px] text-muted-foreground">
              Will save <strong>{Object.values(overrides).filter((v) => v === true).length}</strong> show-overrides
              and <strong>{Object.values(overrides).filter((v) => v === false).length}</strong> hide-overrides.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveAsPresetOpen(false)}>Cancel</Button>
            <Button
              onClick={saveAsCustomPreset}
              disabled={!newPresetLabel.trim() || savingNewPreset}
              data-testid="save-preset-confirm"
            >
              {savingNewPreset ? 'Saving…' : 'Save preset'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
